import { useMemo, useState, useEffect, useRef, useCallback } from "react";
import {
  Box,
  Typography,
  Table,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
} from "@mui/material";
import { CoinRow } from "./component/CoinRow/CoinRow";
import AppleSvg from "../../assets/svg/AppleSvg";
import { styles } from "./stylesTrendingStock";
import CustomSelect from "../../components/UI/CustomSelect/CustomSelect";
import { useGetIndustriesQuery, useGetSectorsQuery } from "@/store/api/watchlistFiltersApi";
import { useGetTrendingStocksQuery } from "../../store/api/trendingStocksApi";
import type { TrendingStocksParams, TrendingStock } from "../../store/api/trendingStocksApiData";
import CustomLoader from "@/components/UI/CustomLoader/CustomLoader";
import ArrowIcon from "@/assets/svg/ArrowIcon";
import LockUnlockSvg from "@/assets/svg/LockUnlockSvg";
import PremiumSubscriptionModal from "@/components/UI/modal/PremiumSubscriptionModal/PremiumSubscriptionModal";
import CustomToast from "@/components/UI/CustomToast/CustomToast";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  allTimeOptions,
  PREMIUM_TIME_OPTIONS,
  TIME_OPTIONS,
  TimeRangeValue,
} from "@/types/timeRanges";
import { useAppSelector } from "@/hooks/useAppSelector";
import theme from "@/theme";
import { normalizeSentiment } from "@/helpers/normalizeSentiment";
import { navigateWithParams } from "@/helpers/navigation";

type CoinRowStock = {
  Icon: React.ElementType;
  name: string;
  symbol: string;
  change: number;
  sentiment: number;
  post: string;
  likes: string;
  percent: number;
  industry: string;
  sector: string;
};

const LIMIT = 20;
const FREE_ROWS_COUNT = 3;

const TrendingStock = () => {
  const [selectedIndustries, setSelectedIndustries] = useState<string[]>([]);
  const [selectedSectors, setSelectedSectors] = useState<string[]>([]);
  const [timeRange, setTimeRange] = useState<TimeRangeValue>("24h");
  const [sortField, setSortField] = useState<"post" | "likes" | "sentiment">("sentiment");
  const [order, setOrder] = useState<"asc" | "desc">("asc");
  const sortFields: ("post" | "likes" | "sentiment")[] = ["post", "likes", "sentiment"];
  const { subscriptionStatus } = useAppSelector((state) => state.auth);
  const hasPremium = subscriptionStatus === "ACTIVE";
  const { data: sectorsData } = useGetSectorsQuery();
  const { data: industriesData } = useGetIndustriesQuery();
  const industriesOptions = industriesData?.industries || [];
  const sectorsOptions = sectorsData?.sectors || [];
  const [searchParams] = useSearchParams();
  const [subtype, setSubtype] = useState<string[]>([]);
  const [allStocks, setAllStocks] = useState<CoinRowStock[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const observer = useRef<IntersectionObserver | null>(null);
  const loadMoreRef = useRef<HTMLDivElement | null>(null);
  const tableContainerRef = useRef<HTMLDivElement | null>(null);
  const unlockButtonRef = useRef<HTMLDivElement | null>(null);
  const [showFloatingUnlock, setShowFloatingUnlock] = useState(false);
  const [floatingCenter, setFloatingCenter] = useState<number | null>(null);
  const [premiumModalOpen, setPremiumModalOpen] = useState(false);
  const [toast, setToast] = useState<{ message: string; success?: boolean } | null>(null);

  const [isFiltersChanged, setIsFiltersChanged] = useState(false);
  const prevFiltersHashRef = useRef<string>("");

  const navigate = useNavigate();
  const getParamsHash = useCallback(
    (
      params: TrendingStocksParams & {
        sectors?: string[];
        industries?: string[];
        categories?: string[];
        subtype?: string[];
        order?: "asc" | "desc";
      }
    ) => {
      return JSON.stringify({
        sortField: params.sortBy,
        order: params.order,
        timeRange: params.time_range,
        industries: params.industries,
        sectors: params.sectors,
        subtype: params.subtype,
      });
    },
    []
  );

  useEffect(() => {
    let filtersChanged = false;

    // --- Industries ---
    const urlIndustries = searchParams.get("industries");
    if (urlIndustries) {
      const industriesArray = urlIndustries.split(",").map((i) => decodeURIComponent(i.trim()));
      if (JSON.stringify(industriesArray) !== JSON.stringify(selectedIndustries)) {
        setSelectedIndustries(industriesArray);
        setSelectedSectors([]);
        filtersChanged = true;
      }
    }

    // --- Sectors ---
    const urlSectors = searchParams.get("sectors");
    if (urlSectors) {
      const sectorsArray = urlSectors
        ? urlSectors.split(",").map((s) => decodeURIComponent(s.trim()))
        : [];
      if (JSON.stringify(sectorsArray) !== JSON.stringify(selectedSectors)) {
        setSelectedSectors(sectorsArray);
        setSelectedIndustries([]);
        filtersChanged = true;
      }
    }

    // --- Subtype ---
    const urlSubtype = searchParams.get("subtype");
    if (urlSubtype) {
      const subtypeArray = urlSubtype.split(",").map((s) => s.trim());
      if (JSON.stringify(subtypeArray) !== JSON.stringify(subtype)) {
        setSubtype(subtypeArray);
        filtersChanged = true;
      }
    }

    // --- Time Range ---
    const urlTime = searchParams.get("timeRange"); // или time_range в зависимости от бэка
    if (urlTime) {
      const availableOptions = hasPremium ? allTimeOptions : TIME_OPTIONS;
      const found = availableOptions.find((o) => o.value === urlTime);
      if (found?.value !== timeRange) {
        setTimeRange(found?.value ?? "");
        filtersChanged = true;
      }
    }

    // --- Sort ---
    const urlSortBy = searchParams.get("sortBy");
    const sortMap: Record<string, "post" | "likes" | "sentiment"> = {
      post: "post",
      likes: "likes",
      sentiment: "sentiment",
    };
    if (urlSortBy && sortMap[urlSortBy] && sortField !== sortMap[urlSortBy]) {
      setSortField(sortMap[urlSortBy]);
      filtersChanged = true;
    }

    const urlOrder = searchParams.get("order") as "asc" | "desc";
    if (urlOrder && urlOrder !== order) {
      setOrder(urlOrder);
      filtersChanged = true;
    }

    if (filtersChanged) {
      setPage(1);
      setIsFiltersChanged(true);
    }
  }, [searchParams, hasPremium]);

  const handleSortClick = (isActive: boolean, field: "post" | "likes" | "sentiment") => {
    if (isActive) {
      setOrder(order === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setOrder("desc");
    }
  };

  const queryParams: TrendingStocksParams & {
    sectors?: string[];
    industries?: string[];
    categories?: string[];
    subtype?: string[];
    order?: "asc" | "desc";
  } = useMemo(() => {
    const params: typeof queryParams = {
      sortBy: sortField,
      order,
      page,
      limit: LIMIT,
    };
    if (timeRange) params.time_range = timeRange;
    if (selectedIndustries.length) params.industries = selectedIndustries;
    if (selectedSectors.length) params.sectors = selectedSectors;
    if (subtype.length && !subtype.includes("All")) {
      params.subtype = subtype;
    }

    return params;
  }, [sortField, order, timeRange, selectedIndustries, selectedSectors, subtype, page]);

  const {
    data: response,
    isLoading,
    isFetching,
  } = useGetTrendingStocksQuery(queryParams, {
    refetchOnMountOrArgChange: true,
  });

  const isDataLoading = isLoading || isFetching;
  const coinRowQueryParams = {
    sectors: selectedSectors,
    industries: selectedIndustries,
    time_range: timeRange,
  };

  const hasBlurredRows = !hasPremium && allStocks.length > FREE_ROWS_COUNT;
  const visibleStocks = hasBlurredRows ? allStocks.slice(0, FREE_ROWS_COUNT) : allStocks;
  const blurredStocks = hasBlurredRows ? allStocks.slice(FREE_ROWS_COUNT) : [];

  const openPremiumModal = () => setPremiumModalOpen(true);
  const closePremiumModal = () => setPremiumModalOpen(false);

  useEffect(() => {
    if (!hasBlurredRows) {
      setShowFloatingUnlock(false);
      return;
    }

    const el = unlockButtonRef.current;
    if (!el) return;

    // Scrolling on this page happens on the outer Layout container, not
    // inside TableContainer, so we track visibility against the viewport.
    const intersectionObserver = new IntersectionObserver(
      ([entry]) => setShowFloatingUnlock(!entry.isIntersecting),
      { root: null, threshold: 0 }
    );

    intersectionObserver.observe(el);

    return () => intersectionObserver.disconnect();
  }, [hasBlurredRows, allStocks]);

  useEffect(() => {
    if (!hasBlurredRows) return;

    const updatePosition = () => {
      const el = tableContainerRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      setFloatingCenter(rect.left + rect.width / 2);
    };

    updatePosition();
    window.addEventListener("resize", updatePosition);
    return () => window.removeEventListener("resize", updatePosition);
  }, [hasBlurredRows]);

  useEffect(() => {
    const currentHash = getParamsHash(queryParams);

    if (prevFiltersHashRef.current === "") {
      prevFiltersHashRef.current = currentHash;
      return;
    }

    if (prevFiltersHashRef.current !== currentHash) {
      setIsFiltersChanged(true);
      prevFiltersHashRef.current = currentHash;
    }
  }, [queryParams, getParamsHash]);

  const wasFetchingRef = useRef(false);

  useEffect(() => {
    if (wasFetchingRef.current && !isFetching && isFiltersChanged) {
      setIsFiltersChanged(false);
    }
    wasFetchingRef.current = isFetching;
  }, [isFetching, isFiltersChanged]);

  useEffect(() => {
    if (!response) return;

    if (response.data.length === 0) {
      setAllStocks([]);
      setHasMore(false);
      return;
    }

    const newStocks: CoinRowStock[] = response.data.map((item: TrendingStock) => {
      const percent =
        item.previousPrice && item.previousPrice !== 0
          ? Number((((item.currentPrice || 0) - item.previousPrice) / item.previousPrice) * 100)
          : 0;

      return {
        Icon: AppleSvg,
        name: item.name || "Name",
        symbol: item.symbol || "",
        change: (item.currentPrice || 0) - (item.previousPrice || 0),
        sentiment: normalizeSentiment(item.sentiment) ?? normalizeSentiment(5),
        post: item.mentionCount?.toString() || "",
        likes: item.likeCount?.toString() || "",
        percent: Number(percent.toFixed(2)),
        industry: selectedIndustries[0] ?? "",
        sector: selectedSectors[0] ?? "",
      };
    });

    if (isFiltersChanged && page === 1) {
      setAllStocks(newStocks);
      setIsFiltersChanged(false);
    } else if (page === 1) {
      setAllStocks(newStocks);
    } else {
      const existingSymbols = new Set(allStocks.map((stock) => stock.symbol));
      const uniqueNewStocks = newStocks.filter((stock) => !existingSymbols.has(stock.symbol));
      setAllStocks((prev) => [...prev, ...uniqueNewStocks]);
    }

    setHasMore(response.meta.total >= LIMIT * page);
  }, [response, page, selectedIndustries, selectedSectors, isFiltersChanged]);

  useEffect(() => {
    setPage(1);
    setAllStocks([]);
    setIsFiltersChanged(true);
  }, [selectedIndustries, selectedSectors, subtype, timeRange, sortField, order]);

  const isDataLoadingRef = useRef(isDataLoading);
  useEffect(() => {
    isDataLoadingRef.current = isDataLoading;
  }, [isDataLoading]);

  useEffect(() => {
    // sentinel only mounts once `!isFiltersChanged && hasMore`, so the
    // effect must re-run when either flips to (re)attach the observer
    if (!hasMore || isFiltersChanged || !loadMoreRef.current) return;

    observer.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !isDataLoadingRef.current) {
          setPage((prev) => prev + 1);
        }
      },
      { threshold: 0.1, rootMargin: "100px" }
    );

    observer.current.observe(loadMoreRef.current);

    return () => observer.current?.disconnect();
  }, [hasMore, isFiltersChanged]);

  useEffect(() => {
    const hasUrlFilters =
      searchParams.has("timeRange") ||
      searchParams.has("type") ||
      searchParams.has("subtype") ||
      searchParams.has("sortBy");

    if (hasUrlFilters) {
      setIsFiltersChanged(true);
    }
  }, [searchParams]);

  const updateFilters = (newFilters: Partial<typeof queryParams> & { timeRange?: string }) => {
    navigateWithParams(navigate, "trending", {
      industries: selectedIndustries,
      sectors: selectedSectors,
      subtype,
      timeRange,
      sortBy: sortField,
      order,
      ...newFilters,
    });
    setPage(1);
    setIsFiltersChanged(true);
  };

  const handleIndustryChange = (value: string[]) => {
    setSelectedIndustries(value);
    setSelectedSectors([]);
    updateFilters({ industries: value, sectors: [] });
  };

  const handleSectorChange = (value: string[]) => {
    setSelectedSectors(value);
    setSelectedIndustries([]);
    updateFilters({ sectors: value, industries: [] });
  };

  const handleTimeRangeChange = (labels: string[]) => {
    const found = allTimeOptions.find((o) => o.label === labels[0]);
    const newTimeRange = found?.value ?? "";
    setTimeRange(newTimeRange);
    updateFilters({ timeRange: newTimeRange });
  };

  const handleSubtypeChange = (value: string[]) => {
    setSubtype(value);
    updateFilters({ subtype: value });
  };

  return (
    <Box>
      <Box sx={styles.container}>
        <Box sx={styles.sortBox}>
          <Box sx={styles.filtersWrapper}>
            <CustomSelect
              options={industriesOptions}
              selected={selectedIndustries}
              onChange={handleIndustryChange}
              placeholder="Industries"
              checkbox
              multiple
              width="241px"
              renderValue={(selected) => {
                if (!selected.length) return "Industries";
                if (selected.length === industriesOptions.length) return "All";
                if (selected.length === 1) return selected[0];
                return `${selected.length} Selected`;
              }}
              disabled={!industriesOptions.length}
              loading={!industriesOptions.length}
            />

            <CustomSelect
              options={sectorsOptions}
              selected={selectedSectors}
              onChange={handleSectorChange}
              placeholder="Sectors"
              checkbox
              multiple
              width="241px"
              renderValue={(selected) => {
                if (!selected.length) return "Sectors";
                if (selected.length === sectorsOptions.length) return "All";
                if (selected.length === 1) return selected[0];
                return `${selected.length} Selected`;
              }}
              disabled={!sectorsOptions.length}
              loading={!sectorsOptions.length}
            />

            <CustomSelect
              options={allTimeOptions.map((o) => o.label)}
              selected={
                timeRange ? [allTimeOptions.find((o) => o.value === timeRange)?.label ?? ""] : []
              }
              onChange={handleTimeRangeChange}
              placeholder="Time Range"
              multiple={false}
              width="160px"
              disabledOptions={!hasPremium ? PREMIUM_TIME_OPTIONS.map((o) => o.label) : []}
            />

            <CustomSelect
              options={["All", "Technical", "Fundamental"]}
              selected={subtype}
              onChange={handleSubtypeChange}
              placeholder="All"
              width={142}
              checkblack={true}
            />
          </Box>
        </Box>

        <TableContainer
          ref={tableContainerRef}
          sx={styles.tableContainer}
          component={Paper}
          className="hide-scrollbar"
        >
          <Box sx={styles.tableRelativeWrapper}>
          <Table stickyHeader>
            <TableHead sx={styles.tableHead}>
              <TableRow>
                <TableCell sx={{ ...styles.tableHeadCell, minWidth: "275px" }}>
                  <Typography sx={styles.tableHeadText}>Stocks</Typography>
                </TableCell>

                {sortFields.map((field) => {
                  const isActive = sortField === field;
                  return (
                    <TableCell key={field} sx={styles.tableHeadCell}>
                      <Box
                        display="flex"
                        alignItems="center"
                        sx={{ cursor: "pointer" }}
                        onClick={() => handleSortClick(isActive, field)}
                      >
                        <Typography sx={styles.tableHeadText}>
                          {field === "post" ? "Post" : field === "likes" ? "Likes" : "Sentiment"}
                        </Typography>
                        <ArrowIcon
                          size={18}
                          direction={isActive && order === "asc" ? "up" : "down"}
                          color={isActive ? theme.palette.custom.mainBlue : "#C4C4C4"}
                        />
                      </Box>
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableHead>

            {isFiltersChanged ? (
              <tbody>
                <TableRow sx={{ height: 200 }}>
                  <TableCell colSpan={4} sx={{ borderBottom: "none", p: 0 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        height: "100%",
                      }}
                    >
                      <CustomLoader size={48} />
                    </Box>
                  </TableCell>
                </TableRow>
              </tbody>
            ) : (
              <>
                {isDataLoading && page === 1 ? (
                  <tbody>
                    <TableRow sx={{ height: 200 }}>
                      <TableCell colSpan={4} sx={{ borderBottom: "none", p: 0 }}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            height: "100%",
                          }}
                        >
                          <CustomLoader size={48} />
                        </Box>
                      </TableCell>
                    </TableRow>
                  </tbody>
                ) : (
                  <tbody>
                    {visibleStocks.map((coin) => (
                      <CoinRow
                        key={coin.symbol}
                        coin={coin}
                        queryParams={coinRowQueryParams}
                      />
                    ))}

                    {blurredStocks.map((coin) => (
                      <CoinRow
                        key={coin.symbol}
                        coin={coin}
                        queryParams={coinRowQueryParams}
                        blurred
                      />
                    ))}
                  </tbody>
                )}
              </>
            )}
          </Table>

          {hasBlurredRows && (
            <Box ref={unlockButtonRef} sx={styles.unlockButtonOverlay}>
              <Button sx={styles.unlockButton} onClick={openPremiumModal}>
                <LockUnlockSvg />
                <Typography sx={styles.unlockButtonText}>Unlock with Premium</Typography>
              </Button>
            </Box>
          )}
          </Box>

          {!isFiltersChanged && hasMore && (
            <Box sx={{ width: "100%", border: "none" }}>
              <Box>
                <div ref={loadMoreRef} style={{ height: 50 }} />
                {isFetching && page > 1 && (
                  <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                    <CustomLoader size={34} />
                  </Box>
                )}
              </Box>
            </Box>
          )}

        </TableContainer>

        {hasBlurredRows && showFloatingUnlock && floatingCenter !== null && (
          <Box
            sx={{
              ...styles.floatingUnlockWrapper,
              left: `${floatingCenter}px`,
            }}
          >
            <Button sx={styles.unlockButton} onClick={openPremiumModal}>
              <LockUnlockSvg />
              <Typography sx={styles.unlockButtonText}>Unlock with Premium</Typography>
            </Button>
          </Box>
        )}
      </Box>

      {toast && (
        <CustomToast
          message={toast.message}
          success={toast.success}
          onClose={() => setToast(null)}
        />
      )}
      <PremiumSubscriptionModal
        open={premiumModalOpen}
        onClose={closePremiumModal}
        setToast={setToast}
      />
    </Box>
  );
};

export default TrendingStock;
